import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatMoney } from "@/lib/api";
import { CHECKOUT } from "@/constants/testIds";

export default function Checkout() {
  const { items, totals, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    company: user?.company || "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    notes: "",
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="font-display text-2xl text-slate-900 mb-4">Your cart is empty</p>
        <button
          onClick={() => nav("/shop")}
          className="px-6 py-3 bg-emerald-900 text-white text-sm rounded-sm hover:bg-emerald-800"
        >
          Continue shopping
        </button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: {
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          company: form.company || null,
          line1: form.line1,
          line2: form.line2 || null,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
          country: form.country,
        },
        notes: form.notes || null,
        origin_url: window.location.origin,
      };
      const r = await api.post("/checkout/session", payload);
      // Save minimal for post-payment display
      sessionStorage.setItem("msp_last_order", JSON.stringify({
        order_id: r.data.order_id,
        session_id: r.data.session_id,
      }));
      // Clear local cart before redirect — order captured server-side
      clear();
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create checkout session");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
      <div className="mb-10">
        <p className="overline text-emerald-800 mb-2">Secure checkout</p>
        <h1 className="font-display text-4xl font-black text-slate-900 tracking-tight">
          Complete your order
        </h1>
        <p className="mt-2 text-sm text-slate-500 flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5" /> Payment processed by Stripe · 256-bit encrypted
        </p>
      </div>

      <form
        data-testid={CHECKOUT.form}
        onSubmit={submit}
        className="grid lg:grid-cols-12 gap-10"
      >
        <div className="lg:col-span-7 space-y-8">
          <Section title="Contact">
            <Field label="Full name" required>
              <input
                data-testid={CHECKOUT.name}
                required value={form.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                className={inputCls}
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email" required>
                <input
                  data-testid={CHECKOUT.email}
                  type="email" required value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Phone" required>
                <input
                  data-testid={CHECKOUT.phone}
                  required value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className={inputCls}
                />
              </Field>
            </div>
            <Field label="Company / Facility (optional)">
              <input
                data-testid={CHECKOUT.company}
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                className={inputCls}
              />
            </Field>
          </Section>

          <Section title="Shipping address">
            <Field label="Street address" required>
              <input
                data-testid={CHECKOUT.line1}
                required value={form.line1}
                onChange={(e) => set("line1", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Apartment / Suite (optional)">
              <input
                data-testid={CHECKOUT.line2}
                value={form.line2}
                onChange={(e) => set("line2", e.target.value)}
                className={inputCls}
              />
            </Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="City" required>
                <input data-testid={CHECKOUT.city} required value={form.city}
                  onChange={(e) => set("city", e.target.value)} className={inputCls} />
              </Field>
              <Field label="State" required>
                <input data-testid={CHECKOUT.state} required value={form.state}
                  onChange={(e) => set("state", e.target.value)} className={inputCls} />
              </Field>
              <Field label="ZIP" required>
                <input data-testid={CHECKOUT.postal} required value={form.postal_code}
                  onChange={(e) => set("postal_code", e.target.value)} className={inputCls} />
              </Field>
            </div>
            <Field label="Country" required>
              <input data-testid={CHECKOUT.country} required value={form.country}
                onChange={(e) => set("country", e.target.value)} className={inputCls} />
            </Field>
          </Section>

          <Section title="Order notes (optional)">
            <textarea
              data-testid={CHECKOUT.notes}
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Delivery instructions, PO reference, etc."
              className={inputCls}
            />
          </Section>
        </div>

        <div className="lg:col-span-5">
          <div className="sticky top-24 bg-white border border-slate-200 rounded-sm p-6">
            <p className="overline text-emerald-800 mb-4">Order summary</p>
            <ul className="divide-y divide-slate-100">
              {items.map((it) => (
                <li key={it.product_id} className="py-3 flex gap-3">
                  <div className="w-14 h-14 bg-slate-50 border border-slate-200 overflow-hidden rounded-sm relative">
                    <img src={it.image_url} alt="" className="w-full h-full object-cover" />
                    <span className="absolute -top-2 -right-2 min-w-[20px] h-5 bg-slate-900 text-white text-xs flex items-center justify-center rounded-full px-1.5">
                      {it.quantity}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 line-clamp-2 leading-snug">{it.name}</p>
                    <p className="text-xs text-slate-500">SKU {it.sku}</p>
                  </div>
                  <p className="text-sm font-medium text-slate-900 whitespace-nowrap">
                    {formatMoney(it.price * it.quantity)}
                  </p>
                </li>
              ))}
            </ul>
            <div className="border-t border-slate-200 mt-4 pt-4 space-y-2 text-sm">
              <Row label="Subtotal" val={formatMoney(totals.subtotal)} />
              <Row label="Shipping" val={totals.shipping === 0 ? "FREE" : formatMoney(totals.shipping)} />
              <Row label="Tax (8%)" val={formatMoney(totals.tax)} />
              <div className="flex justify-between pt-2 border-t border-slate-200 font-display font-bold text-slate-900 text-base">
                <span>Total</span>
                <span>{formatMoney(totals.total)}</span>
              </div>
            </div>

            <button
              data-testid={CHECKOUT.submit}
              type="submit"
              disabled={submitting}
              className="mt-6 w-full py-3.5 bg-emerald-900 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? "Redirecting to Stripe…" : "Pay securely with Stripe"}
              {!submitting && <ShieldCheck className="w-4 h-4" />}
            </button>
            <p className="text-[11px] text-slate-500 mt-3 text-center leading-relaxed">
              You'll be redirected to Stripe to complete payment. Your order is confirmed after payment; the distributor is notified automatically.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2.5 text-sm border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-900/20 focus:border-emerald-900 bg-white";

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6">
      <p className="font-display font-bold text-slate-900 text-lg mb-5">{title}</p>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}

function Row({ label, val }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span>
      <span className="text-slate-900">{val}</span>
    </div>
  );
}
